<script setup>
import { computed } from 'vue'
import { ChevronUp, ChevronDown } from 'lucide-vue-next' // Replace with your icon imports if different

const props = defineProps({

    count: {
        type: Number,
        required: true,
        default: 0
    },

    isExpanded: {
        type: Boolean,
        default: false
    },
    // Unique ID for the controlled element (for aria-controls)
    targetId: {
        type: [String, Number],
        required: true
    },
    // Optional label descriptor for screen readers
    itemContext: {
        type: String,
        default: ''
    },
    // Custom text label if not using default 'order'/'orders'
    singularLabel: {
        type: String,
        default: 'order'
    },
    pluralLabel: {
        type: String,
        default: 'orders'
    },
    dataTrack: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['toggle'])

// Pluralization text
const labelText = computed(() => {
    return props.count === 1 ? props.singularLabel : props.pluralLabel
})

// Full accessibility label
const ariaLabelText = computed(() => {
    const action = props.isExpanded ? 'Hide' : 'Show'
    const context = props.itemContext ? ` for ${props.itemContext}` : ''
    return `${action} ${props.count} ${labelText.value}${context}`
})
</script>

<template>
    <button
        v-if="count > 0"
        type="button"
        class="order-count-btn d-none d-md-inline-flex"
        :class="{ 'is-active': isExpanded }"
        @click="emit('toggle')"
        :aria-expanded="isExpanded"
        :aria-controls="`drawer-orders-${targetId}`"
        :aria-label="ariaLabelText"
        :data-track="dataTrack"
        data-detail="toggle_show_orders"
    >
        <span class="text-nowrap">{{ count }} {{ labelText }}</span>

        <!-- Dynamic indicator chevron -->
        <component
            :is="isExpanded ? ChevronUp : ChevronDown"
            :size="12"
            class="chevron-indicator ms-0.5"
            aria-hidden="true"
        />
    </button>
</template>

<style lang="scss" scoped>
$process-cyan: #0099CC;

/* Theme-Aligned Order Count Pill Button */
.order-count-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    font-size: 0.725rem;
    font-weight: 500;
    line-height: 1.2;
    color: var(--color-gray-800);
    background-color: var(--paper-white);
    border: 1px solid var(--color-gray-400);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.15s ease-in-out;

    span {
        text-box-trim: trim-both;
        text-box-edge: cap alphabetic;
    }

    .icon-package {
        color: var(--halftone-grey);
        transition: color 0.15s ease-in-out;
    }

    &:hover {
        background-color: var(--reg-grey);
        border-color: var(--color-gray-600);
        color: var(--rich-black);

        .icon-package {
            color: var(--rich-black);
        }
    }

    &:focus-visible {
        outline: 2px solid var(--color-blue-600, #1A82A5);
        outline-offset: 1px;
    }

    &.is-active {

        //background-color: rgba(178, 212, 222, 0.35);
        //border-color: var(--color-blue-400, #5897A9);
        //color: #00547b;

        background-color: var(--rich-black, #222);
        border-color: var(--color-black, #000);
        color: var(--paper-white, #fff);

        .icon-package,
        .chevron-indicator {
            color: var(--paper-white, #fff);
        }

        &:hover {
            background-color: var(--color-black, #000);
            border-color: var(--color-black, #000);
        }
    }
}

</style>